import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDashboardStats } from '../../slice/dashboard/Dashboardslice';


export const Dashboard = () => {
  const dispatch = useDispatch();
  const { todayStats, thisMonthStats, totalStats, loading, error } = useSelector(
    (state) => state.dashboard
  );

  useEffect(() => {
    dispatch(fetchDashboardStats());
  }, [dispatch]);

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-wrapper">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="alert alert-danger shadow-sm" role="alert">
          <i className="bx bx-error-circle me-2"></i>
          {error}
        </div>
      </div>
    );
  }

  // Metric card component for reusability
  const MetricCard = ({ title, value, icon, iconColor, isAmount = false, trend = null }) => (
    <div className="metric-card-col">
      <div className="metric-card">
        <div className="metric-card-body">
          <div className="metric-icon-wrapper" style={{ backgroundColor: `${iconColor}15` }}>
            <iconify-icon
              icon={icon}
              className="metric-icon"
              style={{ color: iconColor, fontSize: '30px' }}
            />
          </div>
          <div className="metric-content">
            <p className="metric-label">{title}</p>
            <h3 className="metric-value">
              {isAmount 
                ? `₹ ${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                : value.toLocaleString()}
            </h3>
            {trend && (
              <div className={`metric-trend ${trend.isPositive ? 'trend-up' : 'trend-down'}`}>
                <i className={`bx ${trend.isPositive ? 'bx-trending-up' : 'bx-trending-down'}`}></i>
                <span>{trend.percentage}%</span>
                <span className="trend-label">{trend.label}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="dashboard-container">
      {/* TODAY SECTION */}
      <div className="dashboard-section">
        <div className="section-header">
          <div className="section-title-wrapper">
            <iconify-icon icon="solar:calendar-mark-bold-duotone" className="section-icon Dashicon" style={{fontSize: "30px", color: '#FF6B35' }}></iconify-icon>
            <h2 className="section-title">Today Orders</h2>
          </div>
          <div>
            <button className="btn btn-outline-primary btn-sm" onClick={() => dispatch(fetchDashboardStats())}>
              <i className="bx bx-refresh me-2"></i>
              Refresh
            </button> &nbsp;
            <span className="section-badge badge" style={{ backgroundColor: '#FF6B3520', color: '#FF6B35' }}>Live</span>
          </div>
        </div>
        
        <div className="metrics-grid">
          <MetricCard
            title="Revenue"
            value={todayStats.revenue}
            icon="solar:wallet-money-bold-duotone"
            iconColor="#28a745"
            isAmount={true}
          />
          <MetricCard
            title="Profit"
            value={todayStats.profit}
            icon="solar:chart-2-bold-duotone"
            iconColor="#17a2b8"
            isAmount={true}
          />
          <MetricCard
            title="Orders Received"
            value={todayStats.ordersReceived}
            icon="solar:cart-5-bold-duotone"
            iconColor="#007bff"
          />
          <MetricCard
            title="Orders Delivered"
            value={todayStats.ordersDelivered}
            icon="solar:box-bold-duotone"
            iconColor="#28a745"
          />
          <MetricCard
            title="Orders Accepted"
            value={todayStats.ordersAccepted}
            icon="solar:cart-check-bold-duotone"
            iconColor="#20c997"
          />
          <MetricCard
            title="Orders On The Way"
            value={todayStats.ordersOnTheWay}
            icon="solar:delivery-bold-duotone"
            iconColor="#ffc107"
          />
          <MetricCard
            title="Orders Cancelled"
            value={todayStats.ordersCancelled}
            icon="solar:cart-cross-bold-duotone"
            iconColor="#dc3545"
          />
        </div>
      </div>

      {/* THIS MONTH SECTION */}
      <div className="dashboard-section">
        <div className="section-header">
          <div className="section-title-wrapper">
            <iconify-icon icon="solar:calendar-bold-duotone" className="section-icon" style={{ color: '#00A896', fontSize: '30px' }}></iconify-icon>
            <h2 className="section-title">This Month Orders</h2>
          </div>
          <span className="section-badge badge" style={{ backgroundColor: '#00A89620', color: '#00A896' }}>
            {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
        </div>
        
        <div className="metrics-grid">
          <MetricCard
            title="Revenue"
            value={thisMonthStats.revenue}
            icon="solar:wallet-money-bold-duotone"
            iconColor="#28a745"
            isAmount={true}
          />
          <MetricCard
            title="Profit"
            value={thisMonthStats.profit}
            icon="solar:chart-2-bold-duotone"
            iconColor="#17a2b8"
            isAmount={true}
          />
          <MetricCard
            title="Orders Received"
            value={thisMonthStats.ordersReceived}
            icon="solar:cart-5-bold-duotone"
            iconColor="#007bff"
          />
          <MetricCard
            title="Orders Delivered"
            value={thisMonthStats.ordersDelivered}
            icon="solar:box-bold-duotone"
            iconColor="#28a745"
          />
          <MetricCard
            title="Orders Accepted"
            value={thisMonthStats.ordersAccepted}
            icon="solar:cart-check-bold-duotone"
            iconColor="#20c997"
          />
          <MetricCard
            title="Orders On The Way"
            value={thisMonthStats.ordersOnTheWay}
            icon="solar:delivery-bold-duotone"
            iconColor="#ffc107"
          />
          <MetricCard
            title="Orders Cancelled"
            value={thisMonthStats.ordersCancelled}
            icon="solar:cart-cross-bold-duotone"
            iconColor="#dc3545"
          />
        </div>
      </div>

      {/* TOTAL SECTION */}
      <div className="dashboard-section">
        <div className="section-header">
          <div className="section-title-wrapper">
            <iconify-icon icon="solar:chart-square-bold-duotone" className="section-icon" style={{ color: '#F7931E', fontSize: '30px' }}></iconify-icon>
            <h2 className="section-title">Total Orders</h2>
          </div>
          <span className="section-badge badge" style={{ backgroundColor: '#F7931E20', color: '#F7931E' }}>Total</span>
        </div>
        
        <div className="metrics-grid">
          <MetricCard
            title="Revenue"
            value={totalStats.revenue}
            icon="solar:wallet-money-bold-duotone"
            iconColor="#28a745"
            isAmount={true}
          />
          <MetricCard
            title="Profit"
            value={totalStats.profit}
            icon="solar:chart-2-bold-duotone"
            iconColor="#17a2b8"
            isAmount={true}
          />
          <MetricCard
            title="Orders Received"
            value={totalStats.ordersReceived}
            icon="solar:cart-5-bold-duotone"
            iconColor="#007bff"
          />
          <MetricCard
            title="Orders Delivered"
            value={totalStats.ordersDelivered}
            icon="solar:box-bold-duotone"
            iconColor="#28a745"
          />
          <MetricCard
            title="Orders Cancelled"
            value={totalStats.ordersCancelled}
            icon="solar:cart-cross-bold-duotone"
            iconColor="#dc3545"
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;